import type { NostrEvent } from '@nostrify/nostrify';
import type { MediaItem, MediaKind, MediaUpload } from '@/lib/media';

/** Addressable kind for the private media-library index (see NIP.md). */
export const MEDIA_INDEX_KIND = 39653;
/** Tag used to identify the index as Wikistar's media catalog. */
export const MEDIA_INDEX_TAG = 'wikistar-media-index';

/**
 * Serializes the media library into a compact index document, suitable
 * for a `kind:39653` encrypted event.
 *
 * The index intentionally carries **no binary data** — only sha256,
 * tags, alt text, file metadata, and Blossom URLs — because per the
 * design constraint the media files themselves stay local-only on
 * whatever device holds them. Syncing the index lets a new device
 * *know* your library exists and what's in it; restoring the actual
 * files still requires re-uploading each one locally.
 */
export interface MediaIndexEntry {
  sha256: string;
  name: string;
  type: string;
  kind: MediaKind;
  size: number;
  width?: number;
  height?: number;
  duration?: number;
  tags: string[];
  alt?: string;
  uploads: MediaUpload[];
}

export interface MediaIndexDocument {
  version: 1;
  updatedAt: number;
  entries: MediaIndexEntry[];
}

function serializeEntry(item: MediaItem): MediaIndexEntry {
  return {
    sha256: item.sha256,
    name: item.name,
    type: item.type,
    kind: item.kind,
    size: item.size,
    ...(item.width !== undefined ? { width: item.width } : {}),
    ...(item.height !== undefined ? { height: item.height } : {}),
    ...(item.duration !== undefined ? { duration: item.duration } : {}),
    tags: item.tags,
    ...(item.alt !== undefined ? { alt: item.alt } : {}),
    uploads: item.uploads,
  };
}

export function buildMediaIndex(items: MediaItem[]): MediaIndexDocument {
  return {
    version: 1,
    updatedAt: Date.now(),
    entries: items.map(serializeEntry),
  };
}

/**
 * Rebuilds `MediaItem[]` from a decoded index document, without any
 * binary data attached. Items start as local-only (`hasBlob: false`,
 * no thumbnail) — the index only tells you they exist.
 *
 * `pubkey` is the account doing the restore. The index carries no pubkey
 * of its own (it's NIP-44 encrypted to the owner, so every entry is
 * theirs by definition) — the item is stamped with the restoring account
 * so it lands in the right per-account library.
 */
export function mediaItemsFromIndex(doc: MediaIndexDocument, pubkey: string): MediaItem[] {
  return doc.entries.map((entry) => ({
    sha256: entry.sha256,
    pubkey,
    name: entry.name,
    type: entry.type,
    kind: entry.kind,
    size: entry.size,
    width: entry.width,
    height: entry.height,
    duration: entry.duration,
    tags: entry.tags,
    alt: entry.alt,
    addedAt: doc.updatedAt,
    uploads: entry.uploads,
    hasBlob: false,
  }));
}

/**
 * Decrypts a `kind:39653` media-index event's content back to its
 * document, or returns undefined if it's malformed.
 */
export function parseMediaIndex(event: NostrEvent, content: string): MediaIndexDocument | undefined {
  if (event.kind !== MEDIA_INDEX_KIND) return undefined;
  try {
    const parsed = JSON.parse(content) as Partial<MediaIndexDocument>;
    if (parsed.version !== 1 || !Array.isArray(parsed.entries)) return undefined;
    return {
      version: 1,
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0,
      entries: parsed.entries as MediaIndexEntry[],
    };
  } catch {
    return undefined;
  }
}
